import {
  Order,
  DurationPeriod,
  Status,
  UserInterface as SalesUser,
  StatusHistoryItemInterface,
  getMetadataFromStatusHistoryItem,
  HistoryMetadata,
  Source,
} from '@vendasta/sales-orders';

export const DurationMapping = new Map<DurationPeriod, string>([
  [DurationPeriod.DAY, 'FRONTEND.SALES_UI.DAYS_PLURAL_OR_SINGULAR'],
  [DurationPeriod.WEEK, 'FRONTEND.SALES_UI.WEEKS_PLURAL_OR_SINGULAR'],
  [DurationPeriod.MONTH, 'FRONTEND.SALES_UI.MONTHS_PLURAL_OR_SINGULAR'],
  [DurationPeriod.YEAR, 'FRONTEND.SALES_UI.YEARS_PLURAL_OR_SINGULAR'],
]);

export interface User {
  userId: string;
  fullName?: string;
  email?: string;
}

/**
 *
 * @param userId: must be non empty string
 * @param email: must be non empty string
 * @throws Error if required arguments are not valid
 */
export function userWithEmail(userId: string, email: string): User {
  if (!userId) {
    throw new Error('userId is required');
  }
  if (!email) {
    throw new Error('email is required');
  }
  return {
    userId: userId,
    email: email,
  };
}

/**
 *
 * @param userId: must be non empty string
 * @param fullName: must be non empty string
 * @throws Error if required arguments are not valid
 */
export function userWithName(userId: string, fullName: string): User {
  if (!userId) {
    throw new Error('userId is required');
  }
  if (!fullName) {
    throw new Error('fullName is required');
  }
  return {
    userId: userId,
    fullName: fullName,
  };
}

export class OrderDetailField {
  status: Status;
  date: Date;
  metadata: HistoryMetadata;
  user: SalesUser;
  userInfo: string;
  childFields: OrderDetailFieldChild[];

  private static buildFieldsFromStatusHistory(
    historyItems: StatusHistoryItemInterface[],
    usersMap: { [key: string]: SalesUser },
  ): OrderDetailField[] {
    const fields: OrderDetailField[] = [];
    if (historyItems) {
      historyItems.forEach((historyItem, index) => {
        // If it's the first item, or the current status is different than the previous one add to the toplevel array
        if (index === 0 || historyItems[index - 1].status !== historyItem.status) {
          const field = this.buildFieldFromStatusHistory(historyItem, usersMap, false);
          fields.push(field);
        } else {
          // The statuses are the same so add to the child fields of the last item
          const field = this.buildFieldFromStatusHistory(historyItem, usersMap, true);
          fields[fields.length - 1].childFields.push(field);
        }
      });
    }
    return fields;
  }

  private static buildFieldFromStatusHistory(
    historyItem: StatusHistoryItemInterface,
    usersMap: { [key: string]: SalesUser },
    isChild: boolean,
  ): OrderDetailField {
    const metadata = getMetadataFromStatusHistoryItem(historyItem);
    let userId = historyItem.userId;

    // setup status specific metadata labels
    if (isChild && !!metadata) {
      userId = metadata.userId;
    }

    let user: SalesUser = { email: historyItem.email };
    if (userId in usersMap) {
      user = usersMap[userId];
    }

    const userInfo = this.getNormalizedUserInfo(historyItem);
    return new OrderDetailField(historyItem.status, historyItem.created, metadata, user, userInfo);
  }

  private static getNormalizedUserInfo(item: StatusHistoryItemInterface): string {
    if (item.name) {
      return item.name;
    }

    switch (item.source) {
      case Source.SOURCE_SYSTEM:
        return 'System';
      case Source.SOURCE_SYSTEM_ADMIN:
        return 'System Admin';
      default:
        return '';
    }
  }

  public static createFromOrderWithUsers(
    salesOrder: Order,
    usersMap: { [key: string]: SalesUser },
  ): OrderDetailField[] {
    const additionalFields = this.buildFieldsFromStatusHistory(salesOrder.statusHistory, usersMap);
    return additionalFields;
  }

  constructor(status: Status, date: Date, metadata: HistoryMetadata, user: SalesUser, userInfo: string) {
    this.status = status;
    this.date = date;
    this.metadata = metadata;
    this.user = user;
    this.userInfo = userInfo;
    this.childFields = [];
  }
}

export class OrderDetailFieldChild extends OrderDetailField {}
